import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HiCheckCircle } from 'react-icons/hi';
import { supabase } from '../backend/lib/supabaseClient';
import '../styles/Policy.css';

const STORE_EMAIL = 'celestialstones33@gmail.com';

/*
  Every marketing email has to carry a one-click way out. This is where
  that link lands: /unsubscribe?email=someone@example.com pre-fills the
  address so it really is one click.

  The removal runs through the remove_subscriber() function rather than
  a delete from the browser, because anon holds no DELETE on
  newsletter_subscribers — and shouldn't, or anyone could empty the
  list. See backend/sql/newsletter.sql.
*/
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    const address = email.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setBusy(true);

    const { error: rpcError } = await supabase.rpc('remove_subscriber', {
      p_email: address,
    });

    setBusy(false);

    if (rpcError) {
      setError(
        'We couldn’t process that automatically. Please email ' +
          `${STORE_EMAIL} and we’ll remove you by hand.`
      );
      return;
    }

    /* Deliberately the same message whether or not the address was on
       the list. Saying "you weren't subscribed" would turn this form
       into a way to test whether any given email is one of our
       customers. */
    setDone(true);
  };

  return (
    <section className="policy-page">
      <div className="policy-container">
        <header className="policy-head">
          <h1>Unsubscribe</h1>
          <p className="policy-updated">Leave the Celestial Stones mailing list</p>
        </header>

        <div className="policy-body">
          {done ? (
            <div className="policy-done">
              <HiCheckCircle />
              <h2>You're unsubscribed</h2>
              <p>
                <strong>{email.trim().toLowerCase()}</strong> will no longer
                receive our emails. Nothing else changes — any orders you've
                placed are unaffected, and you can still sign in.
              </p>
              <p className="policy-note">
                If one more email reaches you in the next day or two, it was
                already on its way out. After that, there should be none.
              </p>
              <Link className="policy-btn" to="/shop">
                Back to the shop
              </Link>
            </div>
          ) : (
            <>
              <p className="policy-lead">
                Enter the address you'd like removed and we'll take it off the
                list straight away.
              </p>

              <form className="policy-form" onSubmit={submit}>
                <label>
                  Email address
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    autoFocus
                  />
                </label>

                {error && (
                  <p className="policy-error" role="alert">
                    {error}
                  </p>
                )}

                <div className="policy-actions">
                  <button className="policy-btn" type="submit" disabled={busy}>
                    {busy ? 'Removing…' : 'Unsubscribe'}
                  </button>
                </div>
              </form>

              <h2>Would you rather delete everything?</h2>
              <p>
                Unsubscribing only stops the emails. To have your account and
                personal details erased entirely, use our{' '}
                <Link to="/data-request">Data &amp; Privacy Requests</Link>{' '}
                page.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
